import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, Plus } from "lucide-react";
import AssignAgentDialog from "./components/AssignAgentDialog";
import { useGetDeployedAgentsQuery } from "@/store/admin_api";
import { AI_VOICE_ORCHESTRATORS } from "@/model/interface/AiAgent";

export default function DeployedAgentsAdmin() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { data, isLoading } = useGetDeployedAgentsQuery({ page, size: 50 });
  const [assignOpen, setAssignOpen] = useState(false);

  const agents = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;

  const orchestratorLabel = (orchestrator: string) =>
    AI_VOICE_ORCHESTRATORS.find((option) => option.value === orchestrator)?.label ?? orchestrator;

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Deployed Agents</h3>
          <p className="text-sm text-muted-foreground">
            Every agent running in an organisation. Open one to change its prompt, skills, runtime or voice
            without touching the template.
          </p>
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setAssignOpen(true)}>
          <Plus className="h-4 w-4" /> New agent
        </Button>
      </div>

      <AssignAgentDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        onCreated={(agent) => navigate(`/platform-admin/ai-agents/deployed/${agent.id}`)}
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agent</TableHead>
            <TableHead>Organisation</TableHead>
            <TableHead>Template</TableHead>
            <TableHead>Runtime</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[120px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => (
            <TableRow
              key={agent.id}
              className="cursor-pointer"
              onClick={() => navigate(`/platform-admin/ai-agents/deployed/${agent.id}`)}
            >
              <TableCell className="font-medium">{agent.name}</TableCell>
              <TableCell>{agent.organisation?.name ?? "Not set"}</TableCell>
              <TableCell>{agent.template?.name ?? "Not set"}</TableCell>
              <TableCell className="text-muted-foreground">{orchestratorLabel(agent.orchestrator)}</TableCell>
              <TableCell>
                {agent.lifecycle === "REMOVED" ? (
                  <Badge variant="outline">Removed</Badge>
                ) : (
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge variant={agent.status === "ACTIVE" ? "default" : "secondary"}>{agent.status}</Badge>
                    {agent.lifecycle === "REMOVAL_SCHEDULED" && (
                      <Badge variant="outline">Ends {agent.paidThrough}</Badge>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/platform-admin/ai-agents/deployed/${agent.id}`);
                  }}
                  className="gap-1"
                >
                  Configure
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {agents.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                No deployed agents.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
